import * as React from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import * as classnames from 'classnames';
import { SearchIcon } from 'elc-icons';
import { ContentSpacing, Colors, breakpoint } from 'elc-base-theme';
import { InputField } from 'elc-inputs';
import { translate, ITranslationsCollection } from 'elc-service';
import { handleKeyPress, ACCEPTABLE_KEYS_PRESSED } from '../utils/KeyPressHandler';

import {
    inputBorderTheme,
    customInputThemeMobile,
    inputBorderMobileTheme
} from '../../theme/default-theme';

const SearchOverlayWrapper = styled.div`
    cursor: pointer;
    width: 100%;
    .elc-input-search-gnav-field {
        cursor: pointer;
        pointer-events: none;
        width: 100%;
        left: 0;
        right: 0;
        padding: 0 ${ContentSpacing.space40};
        height: ${ContentSpacing.space44};
        ${customInputThemeMobile};
        ${inputBorderMobileTheme};
        ${breakpoint('desktop')`
            min-width: 292px;
            height: 41px;
            padding: 15px 38px;
            ${inputBorderTheme};
            &:focus {
                ${inputBorderTheme};
            }
        `};
    }

    .elc-icon-search {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        left: ${ContentSpacing.space12};
        [dir='rtl'] & {
            right: ${ContentSpacing.space12};
        }
        :focus {
            outline: ${Colors.info} auto 5px;
        }
    }

    &.elc-search-icon-wrapper-input-has-value {
        .elc-input-search-gnav-field {
            padding: 15px 38px 15px 11px;
            [dir='rtl'] & {
                padding: 15px 11px 15px 38px;
            }
        }
        .elc-icon-search {
            right: ${ContentSpacing.space12};
            left: auto;
            [dir='rtl'] & {
                left: ${ContentSpacing.space12};
                right: auto;
            }
        }
    }

    .elc-floating-label-string {
        display: none;
    }
`;

const acceptedKeys = [ACCEPTABLE_KEYS_PRESSED.ENTER, ACCEPTABLE_KEYS_PRESSED.SPACEBAR];

export interface IOverlayIconProps {
    toggleSearchOverlay(): void;
    isSearchOverlayOpen: boolean;
    translations?: ITranslationsCollection;
    hideGnavInput?: boolean;
}

@translate(['searchPlaceholderLabel', 'searchButtonLabel'])
@observer
export class SearchOverlayActiveScreen extends React.Component<IOverlayIconProps> {
    constructor(props: IOverlayIconProps) {
        super(props);
    }

    public render() {
        const { toggleSearchOverlay, isSearchOverlayOpen } = this.props;
        const translations = this.props.translations as ITranslationsCollection;
        const { searchPlaceholderLabel, searchButtonLabel } = translations;
        const searchInputGnavFieldClassnames = classnames(
            'elc-input-search-gnav-field',
            'js-input-search-gnav-field'
        );
        const svgIconSearchClassnames = classnames('elc-icon-search', 'js-icon-search');
        const circleIconSearchClassnames = classnames(
            'elc-icon-search-circle',
            'js-icon-search-circle'
        );
        const magnifierIconSearchClassnames = classnames(
            'elc-icon-search-magnifier',
            'js-icon-search-magnifier'
        );

        const searchOverlayWrapperClassnames = classnames(
            'elc-search-icon-wrapper',
            'js-search-icon-wrapper'
        );

        return (
            <SearchOverlayWrapper
                className={searchOverlayWrapperClassnames}
                onClick={toggleSearchOverlay}
                tabIndex={0}
                onKeyPress={keyPressed => {
                    handleKeyPress(keyPressed, acceptedKeys, toggleSearchOverlay);
                }}
                role="button"
                aria-expanded={isSearchOverlayOpen}
                aria-label={searchButtonLabel}
                data-test-id="SEA-Icon"
                title={searchButtonLabel}
            >
                {this.props.hideGnavInput ? null : (
                    <InputField
                        name="search-input-gnav"
                        inputLabel="search-input-gnav"
                        inputType="text"
                        placeholder={searchPlaceholderLabel}
                        customClassName={searchInputGnavFieldClassnames}
                        data-test-id="Common-Search"
                    />
                )}
                <SearchIcon
                    className={svgIconSearchClassnames}
                    pathMagnifierClassName={magnifierIconSearchClassnames}
                    pathCircleClassName={circleIconSearchClassnames}
                />
            </SearchOverlayWrapper>
        );
    }
}
